/**
 * Help centre.
 *
 * Three jobs:
 *   1. Get a new dealer from zero to "the AI is answering my customers" —
 *      the onboarding walkthrough and the Twilio setup guide.
 *   2. Answer the one question that actually confuses people (QR or Twilio for
 *      WhatsApp, and why they can't have both).
 *   3. Launch the guided product tour.
 *
 * Content lives in lib/help/content.js as structured data rather than a markdown
 * blob, so it can be searched and deep-linked. It mirrors the two docs under
 * docs/ — change one, change the other.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { ARTICLES, SEARCH_INDEX } from '../lib/help/content';
import { useTour } from '../lib/tour';

/**
 * Articles grouped by their `category`, preserving the order categories first
 * appear in ARTICLES (Get started → Daily use → Setup & admin). Articles with
 * no category fall under a plain "Guides" heading, so nothing is ever dropped.
 */
const GUIDE_GROUPS = (() => {
  const order = [];
  const byCat = new Map();
  for (const a of ARTICLES) {
    const cat = a.category || 'Guides';
    if (!byCat.has(cat)) { byCat.set(cat, []); order.push(cat); }
    byCat.get(cat).push(a);
  }
  return order.map((category) => ({ category, articles: byCat.get(category) }));
})();

/* ─────────────────────────── block renderers ─────────────────────────── */

/**
 * Minimal inline markdown: **bold**, *italic*, `code`. Deliberately not a
 * markdown library — this is three patterns and the content is ours, so the
 * bundle cost of a parser isn't worth it.
 */
function inline(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('`') && p.endsWith('`')) {
      return (
        <code key={i} className="mono" style={{
          fontSize: 11, padding: '1px 5px', borderRadius: 3,
          background: 'var(--bg-glass)', border: '1px solid var(--border-subtle)',
        }}>{p.slice(1, -1)}</code>
      );
    }
    return p;
  });
}

function Callout({ tone, title, text }) {
  const colour = tone === 'warn' ? 'var(--warning)' : 'var(--accent-primary)';
  const bg     = tone === 'warn' ? 'var(--warning-bg)' : 'var(--accent-glow)';
  return (
    <div style={{
      borderLeft: `3px solid ${colour}`,
      background: bg,
      padding: '10px 14px',
      borderRadius: 'var(--radius-sm)',
      margin: '12px 0',
    }}>
      {title && (
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: colour }}>
          {tone === 'warn' ? '⚠ ' : ''}{title}
        </div>
      )}
      <div className="text-sm" style={{ lineHeight: 1.65, color: 'var(--text-secondary)' }}>
        {inline(text)}
      </div>
    </div>
  );
}

function Block({ b }) {
  switch (b.type) {
    case 'p':
      return (
        <p className="text-sm" style={{ lineHeight: 1.75, color: 'var(--text-secondary)', margin: '10px 0' }}>
          {inline(b.text)}
        </p>
      );

    case 'note': return <Callout tone="note" title={b.title} text={b.text} />;
    case 'warn': return <Callout tone="warn" title={b.title} text={b.text} />;

    case 'steps':
      return (
        <ol className="text-sm" style={{ lineHeight: 1.8, paddingLeft: 20, margin: '10px 0', color: 'var(--text-secondary)' }}>
          {b.items.map((it, i) => <li key={i} style={{ marginBottom: 4 }}>{inline(it)}</li>)}
        </ol>
      );

    case 'list':
      return (
        <ul className="text-sm" style={{ lineHeight: 1.8, paddingLeft: 20, margin: '10px 0', color: 'var(--text-secondary)' }}>
          {b.items.map((it, i) => <li key={i} style={{ marginBottom: 4 }}>{inline(it)}</li>)}
        </ul>
      );

    case 'code':
      return (
        <pre className="mono" style={{
          fontSize: 11, lineHeight: 1.7, padding: 12, margin: '12px 0',
          background: 'var(--bg-glass)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-sm)', overflowX: 'auto',
          color: 'var(--text-secondary)', whiteSpace: 'pre',
        }}>{b.text}</pre>
      );

    case 'table':
      return (
        <div style={{ overflowX: 'auto', margin: '12px 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {b.head.map((h, i) => (
                  <th key={i} style={{
                    textAlign: 'left', padding: '7px 10px', fontWeight: 600, fontSize: 11,
                    color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4,
                    borderBottom: '1px solid var(--border-color)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) => (
                    <td key={j} style={{
                      padding: '8px 10px', lineHeight: 1.6,
                      borderBottom: '1px solid var(--border-subtle)',
                      color: j === 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontWeight: j === 0 ? 500 : 400,
                    }}>{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

/* ─────────────────────────── page ─────────────────────────── */

export default function HelpPage() {
  const [activeId, setActiveId] = useState(ARTICLES[0].id);
  const [query, setQuery]       = useState('');
  const navigate = useNavigate();
  const { startTour } = useTour();

  const article = ARTICLES.find((a) => a.id === activeId) || ARTICLES[0];

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    return SEARCH_INDEX.filter((s) => s.haystack.includes(q)).slice(0, 12);
  }, [query]);

  const jumpTo = (articleId, sectionId) => {
    setActiveId(articleId);
    setQuery('');
    // Let the article render before we scroll to the anchor.
    requestAnimationFrame(() => {
      document.getElementById(`sec-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <>
      <TopBar crumbs={['Help']} />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Sidebar ── */}
        <div style={{
          width: 260, padding: 16,
          borderRight: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
          overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          <input
            style={{
              width: '100%', padding: '7px 10px', fontSize: 12,
              background: 'var(--bg-glass)', border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none',
            }}
            placeholder="Search help…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {results ? (
            <div>
              <div className="card-label">{results.length} RESULT{results.length === 1 ? '' : 'S'}</div>
              {results.length === 0 && (
                <div className="mono text-xs text-muted">Nothing matched “{query}”.</div>
              )}
              {results.map((r, i) => (
                <div
                  key={i}
                  onClick={() => jumpTo(r.articleId, r.sectionId)}
                  style={{
                    padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer', marginBottom: 2,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-glass)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{r.heading}</div>
                  <div className="mono text-xs text-muted">{r.articleTitle}</div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Guides grouped by category, in article order. Categories with
                  no `category` field fall under "Guides". */}
              {GUIDE_GROUPS.map((group) => (
                <div key={group.category}>
                  <div className="card-label">{group.category}</div>
                  {group.articles.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => setActiveId(a.id)}
                      style={{
                        padding: '9px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                        marginBottom: 2,
                        background: a.id === activeId ? 'var(--accent-glow)' : 'transparent',
                        color: a.id === activeId ? 'var(--accent-primary-hover)' : 'var(--text-secondary)',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{a.title}</div>
                      <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>{a.minutes}</div>
                    </div>
                  ))}
                </div>
              ))}

              {/* On this page */}
              <div>
                <div className="card-label">ON THIS PAGE</div>
                {article.sections.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => jumpTo(article.id, s.id)}
                    className="text-xs text-muted"
                    style={{ padding: '5px 10px', cursor: 'pointer', lineHeight: 1.5 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-tertiary)')}
                  >
                    {s.heading}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Article ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px' }}>
          <div style={{ maxWidth: 780 }}>

            {/* Quick actions */}
            <div className="grid-2" style={{ gap: 12, marginBottom: 22 }}>
              <div className="card" style={{ padding: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Take the tour</div>
                <div className="mono text-xs text-muted" style={{ marginBottom: 10, lineHeight: 1.6 }}>
                  A two-minute walkthrough of the interface — where leads land, how the AI is
                  monitored, and what to set up first.
                </div>
                <button className="btn small primary" onClick={() => startTour()}>▶ Start tour</button>
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Set up your channels</div>
                <div className="mono text-xs text-muted" style={{ marginBottom: 10, lineHeight: 1.6 }}>
                  Connect Twilio and WhatsApp so SpectraIQ can answer your number’s
                  messages and calls.
                </div>
                <button className="btn small" onClick={() => navigate('/settings')}>Go to Settings → Channels</button>
              </div>
            </div>

            {/* Article header */}
            <div style={{ marginBottom: 18 }}>
              <h1 style={{ fontSize: 22, fontWeight: 650, margin: 0 }}>{article.title}</h1>
              <p className="text-sm text-muted" style={{ margin: '6px 0 0', lineHeight: 1.6 }}>
                {article.blurb}
              </p>
              <div className="flex-row" style={{ gap: 8, marginTop: 10 }}>
                <span className="pill">{article.minutes}</span>
                <span className="pill">{article.audience}</span>
              </div>
            </div>

            {/* Sections */}
            {article.sections.map((s) => (
              <section key={s.id} id={`sec-${s.id}`} style={{ marginBottom: 28, scrollMarginTop: 16 }}>
                <h2 style={{
                  fontSize: 15, fontWeight: 600, margin: '0 0 6px',
                  paddingBottom: 6, borderBottom: '1px solid var(--border-subtle)',
                }}>
                  {s.heading}
                </h2>
                {s.blocks.map((b, i) => <Block key={i} b={b} />)}
              </section>
            ))}

            <div className="mono text-xs text-muted" style={{
              paddingTop: 16, borderTop: '1px solid var(--border-subtle)', lineHeight: 1.7,
            }}>
              Still stuck? Twilio’s own debugger (Monitor → Logs → Errors) shows every failed
              webhook with the exact response it got back — it’s the fastest way to diagnose
              “nothing is arriving”. For anything else, contact your PMDG representative.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
